import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem } from '@mui/material';
import { isSameDay } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormDatePicker, FormSelect } from '../../../common/form';
import { CustomerDto, PricelistClient, PricelistDto } from '../../../common/PricePrgClient';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import { requiredDateSchema } from '../../../utils';
import { getAppSettings } from '../../settings/redux/settingsSelectors';
import { pricePrgError } from '../../ui/redux/uiSlices';
import { getCustomerUi, getPurchasers } from '../redux/customerSelectors';
import { sendPricelistToCustomer, showSendPricelistToCustomerDialog } from '../redux/customerSlices';

const mapStateToProps = (state: RootState) => ({
    customerUi: getCustomerUi(state),
    availableMailSendouts: getAppSettings(state).mailSendouts,
    purchasers: getPurchasers(state)
});

type OwnProps = {
    customer: CustomerDto;
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const pricelistClient = new PricelistClient();

type SendPricelistMail = {
    mailSendoutId: string;
    customerId: number;
    pricelistId: number;
    validAt: Date;
    purchaser?: string | null;
};

const schema: yup.SchemaOf<SendPricelistMail> = yup.object().shape({
    mailSendoutId: yup.string().required().label('Mail sendout'),
    customerId: yup.number().required().label('Customer'),
    pricelistId: yup.number().required().label('Pricelist'),
    validAt: requiredDateSchema.label('Valid from'),
    purchaser: yup.string().nullable().optional().label('Purchaser')
});

const SendPricelistToCustomerDialog = ({
    customer,
    customerUi,
    availableMailSendouts,
    purchasers
}: Props) => {
    const [pricelists, setPricelists] = useState<PricelistDto[]>([]);
    const [validFroms, setValidFroms] = useState<Date[]>([]);
    const dispatch = useDispatch();
    const { control, handleSubmit, setValue, watch, reset, getValues } = useForm<SendPricelistMail>({
        resolver: yupResolver(schema),
        defaultValues: useMemo(
            () => ({
                pricelistId: pricelists.length === 1 ? pricelists[0].pricelistId : -1
            }),
            [pricelists]
        )
    });
    const isSending = customerUi.isSendingPricelistMail;
    const mailSendouts = useMemo(
        () =>
            availableMailSendouts.filter(sendout =>
                customer.customerEmails.some(email => email.mailSendoutIds.includes(sendout.mailSendoutId))
            ),
        [availableMailSendouts, customer]
    );

    const selectedPricelistId = watch('pricelistId');
    useEffect(() => {
        setValue('customerId', customer.customerId);

        async function fetchPricelists() {
            try {
                const pls = await pricelistClient.getPricelistsForCustomer(customer.customerId);
                if (pls.length === 1) {
                    setValue('pricelistId', pls[0].pricelistId);
                }
                setPricelists(pls);
            } catch (err) {
                dispatch(pricePrgError(err));
            }
        }

        fetchPricelists();
    }, [setValue, customer.customerId, dispatch]);

    useEffect(() => {
        if (!selectedPricelistId) {
            return;
        }

        setValidFroms([]);
        reset({
            ...getValues(),
            validAt: undefined
        });

        async function fetchPricelistPeriods() {
            try {
                const plps = await pricelistClient.getPricelistsPeriodsForPricelists([selectedPricelistId]);
                setValidFroms(plps.map(plp => new Date(plp.validFrom)));
            } catch (err) {
                dispatch(pricePrgError(err));
            }
        }

        fetchPricelistPeriods();
    }, [selectedPricelistId, dispatch, getValues, reset]);

    if (customer == null) {
        return null;
    }

    const shouldDisableDate = (date: Date | null) => {
        if (date == null) {
            return false;
        }

        return validFroms.every(validFrom => !isSameDay(validFrom, date));
    };

    const submitForm = (form: SendPricelistMail) => {
        dispatch(
            sendPricelistToCustomer.request({
                mailSendoutId: form.mailSendoutId,
                customerId: form.customerId,
                pricelistId: form.pricelistId,
                validAt: form.validAt.toISOString(),
                purchaser: form.purchaser ?? undefined
            })
        );
    };

    const handleClose = () => {
        dispatch(showSendPricelistToCustomerDialog(false));
    };

    return (
        <Dialog open={true}>
            {/* Check if paper needs to have the above sx*/}
            <DialogTitle>Send pricelist to {customer.name}</DialogTitle>
            <DialogContent>
                <form id="mail-form" onSubmit={handleSubmit(submitForm)}>
                    <Grid sx={{ mt: 1 }} container direction="column" spacing={2}>
                        <Grid item xs={6}>
                            <FormSelect label="Mail sendout" name="mailSendoutId" control={control}>
                                {mailSendouts.map(s => (
                                    <MenuItem key={s.mailSendoutId} value={s.mailSendoutId}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={10}>
                            <FormSelect label="Pricelist" name="pricelistId" control={control}>
                                {pricelists.map(pl => (
                                    <MenuItem key={pl.pricelistId} value={pl.pricelistId}>
                                        {pl.name}
                                    </MenuItem>
                                ))}
                            </FormSelect>
                        </Grid>
                        <Grid item xs={10}>
                            <FormDatePicker
                                name="validAt"
                                label="Valid at"
                                control={control}
                                inputFormat="yyyy-MM-dd"
                                shouldDisableDate={shouldDisableDate}
                                disabled={validFroms.length === 0}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <FormAutocomplete
                                control={control}
                                name="purchaser"
                                label="Purchaser"
                                options={purchasers}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ProgressButton color="primary" type="submit" form="mail-form" isLoading={isSending}>
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};
export default connect(mapStateToProps)(SendPricelistToCustomerDialog);
